// One UI pattern for every new CRM 2.0 list screen (M1-F9, row M1.4c, DC-12):
//   <ListHeader count label addLabel onAdd />          count left, "+ Add …" right
//   <ListTable columns rows onEdit onToggle />         Active switch column, Edit last on the right
//   <FormModal open title isNew onCancel onSave>…</FormModal>   Cancel + Add / Save
//   <DeactivateConfirm open name onCancel onConfirm />          before switching a row off
// Same look as the CRM 2.0 settings screens (Crm2Settings.module.css).
import React from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Modal, Switch } from "@mui/material";
import Button from "../../../Subcomponents/Button";
import Close from "../../Icons/Close";
import cssClasses from "../Settings/Crm2Settings.module.css";

const btnPrimary = { width: "fit-content", color: "#FFFFFF", backgroundColor: "#155EEF", border: "1px solid #155EEF", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };
const btnSecondary = { width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };

export function ListHeader({ count, label = "rows", addLabel, onAdd }) {
  return (
    <div className={cssClasses.listHead}>
      <span className={cssClasses.label}>{count} {label}</span>
      {onAdd && <Button style={btnPrimary} btnName={addLabel || "+ Add"} clicked={onAdd} />}
    </div>
  );
}

// columns: [{ key, label, render?(row) }]; rows need a stable `id`.
export function ListTable({ columns, rows, onEdit, onToggle, activeKey = "isActive", disabled }) {
  if (!rows?.length) return <div className={cssClasses.empty}>Nothing added yet.</div>;
  return (
    <div className={cssClasses.tableCard}>
      <table className={cssClasses.table}>
        <thead>
          <tr>
            {columns.map((c) => <th key={c.key}>{c.label}</th>)}
            {onToggle && <th>Active</th>}
            {onEdit && <th />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              {columns.map((c) => <td key={c.key}>{c.render ? c.render(row) : row[c.key] ?? "—"}</td>)}
              {onToggle && (
                <td style={{ width: "1%" }}>
                  <Switch size="small" checked={!!row[activeKey]} disabled={disabled} inputProps={{ "aria-label": "Active" }} onChange={(e) => onToggle(row, e.target.checked)} />
                </td>
              )}
              {onEdit && (
                <td style={{ width: "1%", textAlign: "right" }}>
                  {!disabled && <button type="button" className={cssClasses.pillEdit} onClick={() => onEdit(row)}>Edit</button>}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FormModal({ open, title, isNew, saving, onCancel, onSave, children }) {
  return (
    <Modal open={!!open} onClose={(_, reason) => reason !== "backdropClick" && !saving && onCancel()} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
      <Box className={cssClasses.modalWrapper}>
        <div className={cssClasses.modalHeader}>
          <span>{title}</span>
          <span className={cssClasses.modalClose} onClick={() => !saving && onCancel()} tabIndex={0} role="button" aria-label="Close"><Close width="1.2rem" height="1.2rem" /></span>
        </div>
        <div className={cssClasses.modalContent}>{children}</div>
        <div className={cssClasses.modalFooter}>
          <Button style={btnSecondary} btnName="Cancel" disable={saving} clicked={onCancel} />
          <Button style={btnPrimary} btnName={isNew ? "Add" : "Save"} disable={saving} isLoading={saving} clicked={onSave} />
        </div>
      </Box>
    </Modal>
  );
}

export function DeactivateConfirm({ open, name, text, onCancel, onConfirm }) {
  return (
    <Dialog open={!!open} onClose={onCancel}>
      <DialogTitle sx={{ fontSize: "1.6rem", fontFamily: "Inter-Medium" }}>{`Switch off ${name || ""}?`}</DialogTitle>
      <DialogContent><p className={cssClasses.headSub}>{text || "It will not be offered for anything new. Records that already use it keep it."}</p></DialogContent>
      <DialogActions sx={{ padding: "1.6rem" }}>
        <Button style={btnSecondary} btnName="Cancel" clicked={onCancel} />
        <Button style={btnPrimary} btnName="Confirm" clicked={onConfirm} />
      </DialogActions>
    </Dialog>
  );
}
